import React from "react";
import { Col } from "react-bootstrap";

import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import { VfxLib } from "../../../assets/vfx";
import Control from "../../../assets/vfx/controls/control";
import { SearchDto, SearchOperator } from "../../../assets/utils/lib/searchDto";

import {
  defaultSearchQuery,
  GRID_ACTION_TYPES,
  Module,
} from "../../../assets/types/common";
import { Constants } from "../../../assets/utils/lib/constants";
import Form from "../../../assets/vfx/form/form";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { ViewProps } from "../props";
import { LookupTypeService } from "./lookupTypeService";

interface LookupTypePageProps<T> {
  controlStyles?: IControlStyles | undefined;
  searchValues?: SearchDto | undefined;
  moduleType: Module;
  onCustomEvent?: (actionType: string, dataId: string) => void;
}

export const LookupTypePage = <T extends ViewProps.CategoryTypeDto>(
  props: LookupTypePageProps<T>
) => {
  const [searchQuery, setSearchQuery] =
    React.useState<SearchDto>(defaultSearchQuery);
  const service: LookupTypeService<T> = new LookupTypeService<T>(
    props.moduleType
  );
  const lookupTypeGridQuery = service.gridQuery(searchQuery);
  const isGridLoading =
    lookupTypeGridQuery.isLoading || lookupTypeGridQuery.isFetching;
  const styles: IControlStyles = props.controlStyles ?? useControlStyles();

  const handleSearch = (values: { name: string | undefined }) => {
    const filters = [];

    if (values.name && values.name.trim() !== Constants.emptyString) {
      filters.push({
        field: "name",
        operator: SearchOperator.Contains,
        value: values.name,
      });
    }

    if (filters.length > 0 && Array.isArray(defaultSearchQuery.filters)) {
      filters.push(...defaultSearchQuery.filters);
    }

    if (filters.length > 0) {
      setSearchQuery({
        ...defaultSearchQuery,
        filters,
      });
    }
  };

  function handleNew() {
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);
  }

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      sorts: prev.sorts,
      pageSize: prev.pageSize ?? 10,
    }));
  };

  const searchContainer: React.JSX.Element = (
    <SearchContainer
      initialValues={{ name: Constants.emptyString }}
      onSearch={handleSearch}
      onClear={handleClear}
      enableReinitialize
    >
      <Col lg={4}>
        <Form.Text name={"name"} label={"Name"} />
      </Col>
    </SearchContainer>
  );

  return (
    <PageContent hideHeader>
        <Control.DataGrid
          dataId="id"
          height="55vh"
          cols={[
            {
              data: "id",
              title: "ID",
              visible: false,
              sortable: false,
              columnType: "number",
              alignment: "right",
            },
            {
              data: "name",
              title: "Name",
              visible: true,
              columnType: "text",
              alignment: "left",
              width: "18%",
            },
            {
              data: "description",
              title: "Description",
              visible: true,
              columnType: "text",
              alignment: "left",
              width: "24%",
            },
          ]}
          actionCol={{
            data: "id",
            title: "Action",
            visible: true,
            columnType: "action",
            alignment: "right",
            width: "100px",
            controlStyles: styles,
            actionButtons: [
              VfxLib.datagridActionButtons({ datagridStyles: styles }).edit,
              VfxLib.datagridActionButtons({ datagridStyles: styles }).view,
            ],
          }}
          onCustomEvent={props.onCustomEvent}
          data={lookupTypeGridQuery?.data?.result ?? []}
          totalRecords={
            lookupTypeGridQuery.data?.totalSize ??
            lookupTypeGridQuery?.data?.result?.length ??
            0
          }
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) => {
            setSearchQuery((query) => ({
              ...query,
              ...patch,
              sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
            }));
          }}
          uiOptions={{
            title: "Lookup Types",
            filterContent: searchContainer,
            primaryAction: {
              text: "Add Lookup Type",
              icon: "bi bi-plus-lg",
              onClick: handleNew,
            },
          }}
        />
    </PageContent>
  );
};
